import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import CustomTable from "components/shared/table";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { ReactQueryQueryError, fetchBatchSample } from "data/queries-common";
import {
  BatchConfigProps,
  StepperControlProps,
} from "pages/geoscape-batch/types";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";

import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";

import { Basemap } from "components/basemap/basemap";
import { ZoomControl } from "pages/geoscape-data/explorer/zoom-control";
import { Layer, MapRef, Source, SymbolLayer } from "react-map-gl";
import { defineSampleRequest } from "../step-upload-input-file/upload-input-file-page";
import { SampleRow, defineColumns, extractRowData } from "./utils";

interface ViewSampleBatchJobProps {}

export const ViewSampleBatchJob = (
  props: ViewSampleBatchJobProps & BatchConfigProps & StepperControlProps,
) => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const mapRef = useRef<MapRef>();

  mapRef.current?.loadImage("/marker.png", (error, image: any) => {
    if (error) throw error;
    if (!mapRef.current?.hasImage("marker"))
      mapRef.current?.addImage("marker", image);
  });

  const initialZoom: number = 3.5;
  const addressZoom: number = 15;
  const [dynamicZoom, setDynamicZoom] = useState(initialZoom);

  const [coordinates] = useState({
    latitude: -27.055646,
    longitude: 134.243199,
  });

  const handleZoom = (zoom: number): void => {
    setDynamicZoom(zoom);
    mapRef.current?.flyTo({ zoom: zoom, duration: 500 });
  };

  const dataLayer: SymbolLayer = {
    id: "point",
    type: "symbol",
    layout: {
      "icon-image": "marker",
      "icon-anchor": "bottom",
      "icon-allow-overlap": true,
    },
    paint: {
      "icon-color": "#FF0000",
      "icon-opacity": 0.8,
    },
  };

  useEffect(() => {
    if (props.batchConfig.hubInfo.sample.request.length === 0) {
      defineSampleRequest(
        props.batchConfig.hubInfo.sample.rows,
        props.batchConfig,
        props.setBatchConfig,
      );
    }
  }, [props.batchConfig.hubInfo.sample.rows]);

  const fetchBatchSamplesQuery = useQuery({
    queryKey: [
      QueryKeys.batchesSampleResponse,
      props.batchConfig.batchInfo.batchId,
      identityId,
    ],
    queryFn: () =>
      fetchBatchSample(
        props.batchConfig.batchInfo.batchId,
        {
          requests: props.batchConfig.hubInfo.sample.request,
          parameters: props.batchConfig.parameters,
        },
        props.batchConfig.type,
      ),
    enabled: props.batchConfig.hubInfo.sample.request.length !== 0,
  });

  useEffect(() => {
    if (fetchBatchSamplesQuery.isSuccess && fetchBatchSamplesQuery.data) {
      props.setBatchConfig((prev) => ({
        ...prev,
        hubInfo: {
          ...prev.hubInfo,
          sample: {
            ...prev.hubInfo.sample,
            response: fetchBatchSamplesQuery.data,
          },
        },
      }));
    }
  }, [fetchBatchSamplesQuery.isSuccess]);

  const resetCompass = () => {
    mapRef.current?.fitBounds(fetchBatchSamplesQuery.data.boundingBox, {
      duration: 2000,
    });
  };

  const handleRowClick = (row: SampleRow) => {
    if (row.longitude === 0 || row.latitude === 0) {
      return;
    }

    mapRef.current?.flyTo({
      center: [row.longitude, row.latitude],
      duration: 2000,
      zoom: addressZoom,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 3: Preview Sample Batch Job</CardTitle>
        <CardDescription>
          View a sample of your batch job to ensure it has been configured
          correctly before submission.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <ReactQueryErrorWrapper
          queries={[fetchBatchSamplesQuery]}
          mutations={[]}
        />

        {fetchBatchSamplesQuery.isError && (
          <Alert variant="destructive">
            <AlertTitle>There was an error sampling the batch job.</AlertTitle>
            <AlertDescription>
              {(fetchBatchSamplesQuery.error as ReactQueryQueryError).message}
            </AlertDescription>
          </Alert>
        )}

        {fetchBatchSamplesQuery.isEnabled &&
          fetchBatchSamplesQuery.isPending && (
            <Skeleton className="h-56 w-full" />
          )}

        {fetchBatchSamplesQuery.isEnabled &&
          fetchBatchSamplesQuery.isPending && (
            <Skeleton className="h-175 w-full" />
          )}

        {fetchBatchSamplesQuery.isSuccess && (
          <CustomTable
            rows={extractRowData(fetchBatchSamplesQuery.data)}
            columns={defineColumns(props.batchConfig.type)}
            isLoading={false}
            overlayText={"No sample data, is your input file empty?"}
            hideFooter={true}
            onRowClick={handleRowClick}
          />
        )}

        {props.batchConfig.hubInfo.sample.request.length === 0 && (
          <Alert variant="warning">
            <AlertDescription>
              If there are no results, there may be something wrong with your
              input file. Please re-upload your input file and try again before
              contacting support.
            </AlertDescription>
          </Alert>
        )}

        {fetchBatchSamplesQuery.isSuccess && (
          <Basemap
            // @ts-ignore
            mapRef={mapRef}
            latitude={coordinates.latitude}
            longitude={coordinates.longitude}
            zoom={initialZoom}
            onZoom={(zoom: any) => setDynamicZoom(zoom)}
            style={{
              width: "100%",
              height: "700px",
            }}
            minZoom={0}
            maxZoom={15}
          >
            <Source
              type="geojson"
              data={{
                ...fetchBatchSamplesQuery.data.featureCollection,
                features:
                  fetchBatchSamplesQuery.data.featureCollection.features.filter(
                    (feature: any) => feature !== null,
                  ),
              }}
            >
              <Layer {...dataLayer} />
            </Source>
            <div className="absolute right-8 top-8 flex flex-col gap-4 justify-end">
              <ZoomControl
                resetCompass={resetCompass}
                dynamicZoom={dynamicZoom}
                onZoom={handleZoom}
              />
            </div>
          </Basemap>
        )}
      </CardContent>
      <CardFooter className="flex flex-row justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => props.setActiveStep(Math.max(props.activeStep - 1, 0))}
        >
          Back
        </Button>

        <Button
          onClick={() => {
            props.saveBatchConfig(
              props.batchConfig,
              Math.min(props.activeStep + 1, props.maxSteps),
            );
          }}
          disabled={props.isSaveBatchConfigLoading}
        >
          {props.isSaveBatchConfigLoading && <Spinner />}
          Continue
        </Button>
      </CardFooter>
    </Card>
  );
};
